import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Button, Modal, Skeleton, toast } from "../ui";
import { escrowService } from "../../api/services/escrow.service";
import type { AffiliatePrompt } from "../../api/services/affiliates.service";
import EscrowChoiceForm from "./EscrowChoiceForm";
import { escrowKey } from "./escrowShared";

interface EscrowChoiceModalProps {
  prompt: AffiliatePrompt;
  /** Answered (or nothing left to answer) */
  onDone: () => void;
  /** Closed without answering: shown again next visit */
  onLater: () => void;
}

/**
 * PRD 9.1: the seller's acceptance pop-up (an Epic 8 server-side prompt, kind escrow_choice).
 * Wording is admin config pending RESPA counsel review.
 */
export default function EscrowChoiceModal({ prompt, onDone, onLater }: EscrowChoiceModalProps) {
  const queryClient = useQueryClient();
  const bidId = prompt.bidId ?? "";
  const { data: view, isLoading, isError } = useQuery({
    queryKey: escrowKey(bidId),
    queryFn: () => escrowService.get(bidId),
    enabled: !!bidId,
    retry: false,
  });

  // Nothing to choose any more (opened elsewhere, cancelled, or no offer on the prompt)
  const stale = !bidId || isError || (!!view && !view.canChoose);
  const closed = useRef(false);
  useEffect(() => {
    if (stale && !closed.current) {
      closed.current = true;
      // Hide for this visit only; no refetch loop on a persistent error
      onLater();
    }
  }, [stale, onLater]);
  if (stale) return null;

  return (
    <Modal
      open
      onClose={onLater}
      title={view?.wording.title ?? "Choose your escrow and title"}
      description={prompt.listingAddress}
      maxWidth="max-w-lg"
    >
      {isLoading || !view ? (
        <Skeleton className="h-40 w-full rounded-xl" />
      ) : (
        <div className="space-y-3" data-testid="affiliate-prompt-escrow_choice">
          <EscrowChoiceForm
            view={view}
            onDone={(next) => {
              queryClient.setQueryData(escrowKey(bidId), next);
              toast.success(next.status === "opened" ? "Escrow opened" : "We emailed you the terms");
              onDone();
            }}
            secondary={
              <Button type="button" variant="secondary" onClick={onLater}>
                Decide later
              </Button>
            }
          />
          <p>
            <Link
              to={`/listing/${view.listingId}/bid/${view.bidId}`}
              className="text-meta font-medium text-brand-700 hover:underline"
            >
              Open the deal
            </Link>
          </p>
        </div>
      )}
    </Modal>
  );
}
